import {
  TextNode,
  SerializedTextNode,
  LexicalNode,
  NodeKey,
  $applyNodeReplacement,
  EditorConfig,
  DOMConversionMap,
  Spread,
} from 'lexical';

export type SerializedWhatsAppStrikethroughNode = Spread<
  {
    type: 'whatsapp-strikethrough';
  },
  SerializedTextNode
>;

/**
 * WhatsApp Strikethrough Node
 * Uses tilde (~text~) for strikethrough formatting
 */
export class WhatsAppStrikethroughNode extends TextNode {
  static getType(): string {
    return 'whatsapp-strikethrough';
  }

  static clone(node: WhatsAppStrikethroughNode): WhatsAppStrikethroughNode {
    return new WhatsAppStrikethroughNode(node.__text, node.__key);
  }



  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement('s');
    element.className = config.theme.whatsappStrikethrough || '';
    element.textContent = this.__text;
    return element;
  }

  updateDOM(prevNode: WhatsAppStrikethroughNode, dom: HTMLElement): boolean {
    // If the text content has changed, update the DOM
    const prevText = prevNode.__text;
    const currentText = this.__text;
    
    if (prevText !== currentText) {
      dom.textContent = currentText;
    }
    
    // Return false because we handle the update ourselves
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      s: () => ({
        conversion: () => ({ node: null }),
        priority: 1,
      }),
      del: () => ({
        conversion: () => ({ node: null }),
        priority: 1,
      }),
      strike: () => ({
        conversion: () => ({ node: null }),
        priority: 1,
      }),
    };
  }

  static importJSON(serializedNode: SerializedWhatsAppStrikethroughNode): WhatsAppStrikethroughNode {
    const node = $createWhatsAppStrikethroughNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedWhatsAppStrikethroughNode {
    return {
      ...super.exportJSON(),
      type: 'whatsapp-strikethrough',
    };
  }

  exportMarkdown(): string {
    return `~${this.__text}~`;
  }
}

export function $createWhatsAppStrikethroughNode(text: string): WhatsAppStrikethroughNode {
  return $applyNodeReplacement(new WhatsAppStrikethroughNode(text));
}

export function $isWhatsAppStrikethroughNode(
  node: LexicalNode | null | undefined
): node is WhatsAppStrikethroughNode {
  return node instanceof WhatsAppStrikethroughNode;
}
